import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    CircularProgress,
    Alert,
    Grid,
    Chip,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Skeleton,
    Paper,
    Button
} from '@mui/material';
import { Search, Check, RestartAlt } from '@mui/icons-material';
import apiClient from '../../../../../services/api/ApiClient';

interface Font {
    name: string;
    category: string;
    variants: string[];
    previewUrl: string;
}

interface FontSelectorProps {
    label: string;
    description: string;
    selectedFont: string | null;
    onFontChange: (font: string | null) => void;
    defaultFontLabel?: string;
    hideHeader?: boolean;
}

const CATEGORIES = [
    { value: '', label: 'All' },
    { value: 'sans-serif', label: 'Sans Serif' },
    { value: 'serif', label: 'Serif' },
    { value: 'display', label: 'Display' },
    { value: 'handwriting', label: 'Handwriting' },
    { value: 'monospace', label: 'Monospace' },
];

const PREVIEW_TEXT = 'The quick brown fox jumps over the lazy dog';

// Global set to track loaded fonts across all instances
const globalLoadedFonts = new Set<string>();

// Load a Google Font stylesheet
const loadGoogleFont = (fontName: string): void => {
    if (globalLoadedFonts.has(fontName)) return;
    globalLoadedFonts.add(fontName);
    
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;500;600;700&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
};

export const FontSelector: React.FC<FontSelectorProps> = ({
    label,
    description,
    selectedFont,
    onFontChange,
    defaultFontLabel = 'Default (Neue Haas)',
    hideHeader = false
}) => {
    const [fonts, setFonts] = useState<Font[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');
    const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set(globalLoadedFonts));
    
    const gridRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    // Fetch fonts from API
    const fetchFonts = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const params = new URLSearchParams({ sort: 'popularity', limit: '100' });
            if (category) params.append('category', category);
            if (search) params.append('search', search);

            const response = await apiClient.get<{ fonts: Font[] }>(`/customization/fonts?${params}`);
            setFonts(response.data.fonts || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load fonts');
        } finally {
            setLoading(false);
        }
    }, [category, search]);

    // Debounced fetch
    useEffect(() => {
        const debounce = setTimeout(fetchFonts, 300);
        return () => clearTimeout(debounce);
    }, [fetchFonts]);

    // Load selected font if it's a Google Font
    useEffect(() => {
        if (selectedFont && !globalLoadedFonts.has(selectedFont)) {
            loadGoogleFont(selectedFont);
            setLoadedFonts(new Set(globalLoadedFonts));
        }
    }, [selectedFont]);

    // Setup IntersectionObserver for lazy loading fonts
    useEffect(() => {
        // Clean up previous observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // Create new observer
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const fontName = entry.target.getAttribute('data-font-name');
                        if (fontName && !globalLoadedFonts.has(fontName)) {
                            loadGoogleFont(fontName);
                            setLoadedFonts(new Set(globalLoadedFonts));
                        }
                        observerRef.current?.unobserve(entry.target);
                    }
                });
            },
            { 
                root: gridRef.current,
                rootMargin: '50px',
                threshold: 0.1
            }
        );

        // Observe all font cards
        const fontCards = gridRef.current?.querySelectorAll('[data-font-name]');
        fontCards?.forEach(card => {
            observerRef.current?.observe(card);
        });

        return () => {
            observerRef.current?.disconnect();
        };
    }, [fonts, loading]);

    const handleCategoryChange = (_: React.MouseEvent<HTMLElement>, newCategory: string | null) => {
        setCategory(newCategory || '');
    };

    const handleResetToDefault = () => {
        onFontChange(null);
    };

    const isUsingDefault = selectedFont === null;

    return (
        <Box sx={{ mb: hideHeader ? 0 : 4 }}>
            {!hideHeader && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                        <Typography variant='h6' gutterBottom sx={{ mb: 0 }}>
                            {label}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            {description}
                        </Typography>
                    </Box>
                    {!isUsingDefault && (
                        <Button
                            variant='outlined'
                            size='small'
                            startIcon={<RestartAlt />}
                            onClick={handleResetToDefault}
                        >
                            Reset to Default
                        </Button>
                    )}
                </Box>
            )}

            {/* Current Selection */}
            <Paper
                elevation={0}
                sx={{ 
                    p: 2, 
                    mb: 2,
                    border: 2,
                    borderColor: isUsingDefault ? 'success.main' : 'primary.main',
                    borderRadius: 2,
                    backgroundColor: 'background.default'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                            label={isUsingDefault ? defaultFontLabel : selectedFont}
                            color={isUsingDefault ? 'success' : 'primary'}
                            size='small'
                        />
                        {isUsingDefault && (
                            <Typography variant='caption' color='text.secondary'>
                                Using local font file
                            </Typography>
                        )}
                    </Box>
                    {hideHeader && !isUsingDefault && (
                        <Button
                            variant='text'
                            size='small'
                            startIcon={<RestartAlt />}
                            onClick={handleResetToDefault}
                        >
                            Reset
                        </Button>
                    )}
                </Box>
                <Typography 
                    sx={{ 
                        fontFamily: isUsingDefault ? 'primaryFont' : `'${selectedFont}', sans-serif`,
                        fontSize: '1.25rem'
                    }}
                >
                    {PREVIEW_TEXT}
                </Typography>
            </Paper>

            {error && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Search */}
            <TextField
                fullWidth
                size='small'
                label='Search fonts'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position='start'>
                            <Search />
                        </InputAdornment>
                    )
                }}
            />

            {/* Category Filter */}
            <Box sx={{ mb: 2, overflowX: 'auto' }}>
                <ToggleButtonGroup
                    value={category}
                    exclusive
                    onChange={handleCategoryChange}
                    size='small'
                    sx={{ flexWrap: 'nowrap' }}
                >
                    {CATEGORIES.map(cat => (
                        <ToggleButton 
                            key={cat.value} 
                            value={cat.value}
                            sx={{ 
                                whiteSpace: 'nowrap',
                                px: 1.5,
                                py: 0.5
                            }}
                        >
                            {cat.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {/* Font count */}
            {!loading && (
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                    {fonts.length} fonts available
                </Typography>
            )}

            {/* Font Grid */}
            <Box ref={gridRef} sx={{ maxHeight: 300, overflow: 'auto' }}>
                {loading ? (
                    <Grid container spacing={1}>
                        {[...Array(6)].map((_, i) => (
                            <Grid item xs={6} sm={4} key={i}>
                                <Skeleton variant='rectangular' height={80} sx={{ borderRadius: 1 }} />
                            </Grid>
                        ))}
                    </Grid>
                ) : fonts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography color='text.secondary'>
                            No fonts found. Try a different search or category.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={1}>
                        {fonts.map(font => (
                            <Grid item xs={6} sm={4} key={font.name}>
                                <Paper
                                    data-font-name={font.name}
                                    elevation={0}
                                    onClick={() => onFontChange(font.name)}
                                    sx={{
                                        p: 1.5,
                                        cursor: 'pointer',
                                        border: 2,
                                        borderColor: selectedFont === font.name ? 'primary.main' : 'divider',
                                        borderRadius: 1,
                                        backgroundColor: selectedFont === font.name ? 'action.selected' : 'background.paper',
                                        transition: 'all 0.2s',
                                        position: 'relative',
                                        minHeight: 80,
                                        '&:hover': {
                                            borderColor: 'primary.light',
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                >
                                    {selectedFont === font.name && (
                                        <Check 
                                            sx={{ 
                                                position: 'absolute', 
                                                top: 4, 
                                                right: 4, 
                                                color: 'primary.main',
                                                fontSize: 16
                                            }} 
                                        />
                                    )}
                                    <Typography 
                                        variant='caption' 
                                        color='text.secondary'
                                        sx={{ 
                                            display: 'block',
                                            mb: 0.5,
                                            fontSize: '0.7rem',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {font.name}
                                    </Typography>
                                    <Typography 
                                        sx={{ 
                                            fontFamily: loadedFonts.has(font.name) 
                                                ? `'${font.name}', sans-serif` 
                                                : 'inherit',
                                            fontSize: '1.1rem',
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        Abc 123
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default FontSelector;
